import { Link, useParams } from "react-router-dom";

import { useProject } from "@/api/projects";
import { ApiError } from "@/api/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

export function SubmitPickerPage() {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);

  if (project.isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-6 text-sm text-muted-foreground">
        Loading project…
      </div>
    );
  }

  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <Card elevation="raised">
        <CardHeader>
          <CardTitle>{isNotFound ? "Project not found" : "Could not load project"}</CardTitle>
          <CardDescription>
            {isNotFound
              ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
              : "The FlowStack server returned an error."}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!project.data) return null;

  return (
    <div className="space-y-6">
      <div>
        <p className="label-micro text-muted-foreground">
          <Link to="/projects" className="hover:underline">
            Projects
          </Link>{" "}
          /{" "}
          <Link to={`/projects/${project.data.slug}`} className="hover:underline">
            {project.data.slug}
          </Link>{" "}
          / submit
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Submit a workload</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A Tile is a single computation; a Flow chains tiles into the full AFFDO pipeline (or a
          custom template).
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Link
          to={`/projects/${project.data.slug}/submit/tile`}
          className="block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Card elevation="raised" className="h-full transition-colors hover:bg-accent/30">
            <CardHeader>
              <CardTitle>Submit a Tile</CardTitle>
              <CardDescription>One containerized step.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Pick from the catalog (e.g. <span className="font-mono">affdo.amber_topology</span>,{" "}
                <span className="font-mono">amberflow.binding_pose_refinement</span>) and supply its
                declared inputs and parameters. Best when you need exactly one computation — running
                a quick force-field assembly or refining a single pose.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Fast: minutes to a few hours per tile.
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/projects/${project.data.slug}/submit/flow`}
          className="block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Card elevation="raised" className="h-full transition-colors hover:bg-accent/30">
            <CardHeader>
              <CardTitle>Submit a Flow</CardTitle>
              <CardDescription>End-to-end AFFDO pipeline.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Pick a Flow template (<span className="font-mono">full_affdo_sequential</span> or{" "}
                <span className="font-mono">full_affdo_wavefront</span>) and supply the ligand
                Molecule. The runner expands the template into per-fragment + per-torsion children
                and produces a ForceFieldBundle v1.1.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Slow: hours to days. Sequential trades walltime for per-scan-point visibility;
                wavefront is faster overall when GPU resources are scarce.
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      <Card elevation="raised">
        <CardHeader>
          <CardTitle className="text-base">Prefer the SDK?</CardTitle>
          <CardDescription>
            Every submission the GUI builds can also be sent via the SDK. See{" "}
            <Link to="/settings" className="hover:underline">
              Settings
            </Link>{" "}
            for the CLI snippet.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}
